import { CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useForm } from 'react-hook-form';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { motion } from 'framer-motion';
import { z } from 'zod';
import React from 'react';

export const courseFormSchema = z.object({
  courseName: z.string().min(3, {
    message: "Course title must be at least 3 characters.",
  }),
  courseDescription: z.string().min(10, {
    message: "Description must be at least 10 characters.",
  }),
  category: z.string().min(1, {
    message: "Please select a category.",
  }),
  duration: z.number().min(1, {
    message: "Duration is required.",
  }),
  level: z.string().min(1, {
    message: "Please select a level.",
  }),
  price: z.number().min(1, {
    message: "Price is required.",
  }),
  status: z.string().min(1, {
    message: "Please select a status.",
  }),
  instructor: z.string().min(1, {
    message: "Instructoer is required"
  }),
  rating: z.number().min(1, {
    message: "Rating is required"
  }),
  additionalMaterials : z.array(z.string().min(1, {
    message: "Additional materials are required"
  })).optional()

});

export type CourseFormValues = z.infer<typeof courseFormSchema>;

interface GeneralInfoTabProps {
  formData: {
    courseName: string;
    description?: string;
    courseDescription?: string
    category: string;
    duration: string;
    level: string;
    price: string;
    status: string;
    rating:number;
    instructor:string;
  };
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (name: string, value: string) => void;
  form: any;
  categories: any;
}

const GeneralInfoTab = ({ formData, handleChange, handleSelectChange, form, categories }: GeneralInfoTabProps) => {
  const categories1 = [
    {
      "id": "6K7GyFsKKyrgwSIkAb1V",
      "category": "Artificial Intelligence"
    },
    {
      "id": "9MAvSbeWEOH1nwiuv985",
      "category": "Web Development"
    },
    {
      "id": "Cz0X6bxHB2W9L7qT4Tqw",
      "category": "Backend Development"
    },
    {
      "id": "L8TF6RthbzdIcTCxM8UN",
      "category": "UI UX Design"
    },
    {
      "id": "ZxboUl5sby1D3c3lN8w1",
      "category": "Business Analyst"
    },
    {
      "id": "gjvs8oNkIz6j6FEITlHn",
      "category": "Application Development"
    },
    {
      "id": "iSQfAsmtynXa1s06LDjY",
      "category": "Data Science & Artificial Intelligence"
    },
    {
      "id":"Mq5DziadA0ykFsvOnsYo",
      "category":"Astrology / Numerology"
    },{
      "category" :"Cloud / SRE / CI CD",
      "id":"MPpBGZC6y8smY6Z9TxgD"
    }
  ];

  const instructors = [
    {
      "name": "Sagar Reddy Guvvala"
    },
    {
      "name": "Panyam Sindhuja"
    },
    {
      "name": "Shaik Arshiya"
    },
    {
      "name": "Ajay Prammasai"
    },
    {
      "name": "Shaik Saddik"
    },
    {
      "name": "Shaik Yasdan"
    },
    {
      "name": "Ponnapudi Chandana"
    },
     {
      "name": "Siddhu Penchal Das"
    }

  ];

  return (
    <CardContent className="space-y-4 pt-2">
      <Form {...form}>
        <form className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="courseName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Course Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Introduction to Programming"
                      {...field}
                      onChange={handleChange} // Call handleChange when the input value changes  
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleSelectChange('category', value); // Update form and state  
                    }}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories1.map((category,i) => (
                        <SelectItem key={category.id+i+1} value={category.category}>
                          {category.category} {/* Assuming category has a 'name' field */}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="courseDescription"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Provide a detailed description of your course"
                    className="min-h-[100px]"
                    {...field}
                    onChange={(e) => {
                      handleChange(e); // Call handleChange  
                      field.onChange(e.target.value); // Also call the form field's onChange  
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration (hours)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="60"
                      min="1"
                      {...field}
                      onChange={handleChange} // Call handleChange  
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="level"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Level</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleSelectChange('level', value); // Update form and state  
                    }}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Beginner">Beginner</SelectItem>
                      <SelectItem value="Intermediate">Intermediate</SelectItem>
                      <SelectItem value="Advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Price (₹)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="1000.00"
                      min="0"
                      {...field}
                      onChange={handleChange} // Call handleChange  
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleSelectChange('status', value); // Update form and state  
                    }}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Draft">Draft</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="instructor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Instructor</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleSelectChange('instructor', value); // Update form and state  
                    }}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Instructoer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {instructors.map((insturctor: any, i: any) => (
                        <SelectItem key={insturctor.name + i + 1} value={insturctor.name}>
                          {insturctor.name} {/* Assuming category has a 'name' field */}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rating <span className="taiwanland-star">★</span> </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="4"
                      {...field}
                      onChange={handleChange} // Call handleChange  
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
    </CardContent>
  );
};

React.memo(GeneralInfoTab);
export default GeneralInfoTab;  